/**
 * Execução automática de um guia (vem do ManualMaker, mmguide.js): por passo, a extensão
 * faz sozinha («auto»), pede confirmação («confirm») ou deixa a pessoa fazer («user»).
 * Os passos de escrita só são feitos pela extensão com um valor definido pelo autor:
 * uma data relativa ao dia da execução, um texto fixo ou uma pergunta no início.
 * O texto gravado (typed) nunca é usado sozinho como valor.
 *
 * As regras de segurança ganham sempre à marca do autor (effectiveRun): um campo
 * sensível é sempre da pessoa, e uma escrita sem valor também.
 */
import type { Locale } from '@nolens/i18n';
import type { Guide, Step } from './steps';
import { dateFormatLabel, datePresetLabel, stepLabel } from './ui-text';

export type RunMark = 'auto' | 'confirm' | 'user';

export const RUN_MARKS: RunMark[] = ['auto', 'confirm', 'user'];

/** Datas relativas ao dia da execução (para filtros de relatórios); nomes em ui-text.ts. */
export const DATE_PRESETS = [
  'today',
  'yesterday',
  'days_ago_7',
  'days_ago_30',
  'week_start',
  'prev_week_start',
  'prev_week_end',
  'month_start',
  'prev_month_start',
  'prev_month_end',
  'year_start',
] as const;

export type DatePreset = (typeof DATE_PRESETS)[number];

export const DATE_FORMATS = [
  'dd/mm/aaaa',
  'aaaa-mm-dd',
  'mm/dd/aaaa',
  'dd-mm-aaaa',
  'dd.mm.aaaa',
] as const;

export type DateFormat = (typeof DATE_FORMATS)[number];

/** Valor de um passo de escrita na execução. */
export type RunValue =
  | { kind: 'date'; preset: DatePreset; format: DateFormat }
  | { kind: 'text'; text: string }
  | { kind: 'ask'; label?: string };

/** O que a execução precisa de um passo (o resto do Step não interessa aqui). */
type RunStep = Pick<Step, 'kind' | 'target' | 'option' | 'run' | 'value'> &
  Partial<Pick<Step, 'menu' | 'shortcut' | 'unverified'>>;

// Botões que costumam alterar dados: pedem confirmação. Palavras inteiras («Pagamentos»
// não conta como «pagar»). «Transferir» fica de fora (em pt-PT é descarregar) e «Limpar»
// também (filtros). Sem nenhum texto (só ícone) também pede confirmação.
const RISKY_RE = new RegExp(
  '\\b(' +
    [
      'guardar',
      'salvar',
      'gravar',
      'submeter',
      'enviar',
      'confirmar',
      'aprovar',
      'autorizar',
      'rejeitar',
      'recusar',
      'reprovar',
      'apagar',
      'eliminar',
      'excluir',
      'remover',
      'deletar',
      'cancelar',
      'anular',
      'estornar',
      'pagar',
      'comprar',
      'encomendar',
      'finalizar',
      'concluir',
      'publicar',
      'assinar',
      'encerrar',
      'bloquear',
      'desativar',
      'desactivar',
      'repor',
      'save',
      'submit',
      'send',
      'confirm',
      'approve',
      'reject',
      'delete',
      'remove',
      'cancel',
      'pay',
      'buy',
      'purchase',
      'publish',
      'sign',
      'reset',
      'disable',
    ].join('|') +
    ')\\b',
  'i',
);

const SEARCH_RE = /\b(pesquis|procur|busca|search|filtr|find|localiz|consult)/;

const plain = (s: unknown) =>
  String(s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/**
 * Marca sugerida para um passo: sensível ou escrita sem valor → «user»; clique num botão
 * que costuma alterar dados (ou sem texto) → «confirm»; o resto → «auto». Na app de Mac: um
 * atalho pede sempre confirmação (⌘S guarda, ⌘W fecha, ⌘Q sai); um menu, conforme o item.
 */
export function suggestRun(step: RunStep): RunMark {
  const t = step.target ?? ({} as NonNullable<Step['target']>);
  if (t.sensitive) return 'user';
  switch (step.kind) {
    case 'navigate':
    case 'tab':
    case 'app':
    case 'text':
      return 'auto';
    case 'shortcut':
      return 'confirm';
    case 'menu': {
      const item = step.menu?.[step.menu.length - 1] ?? '';
      if (!item.trim()) return 'confirm';
      return RISKY_RE.test(plain(item)) ? 'confirm' : 'auto';
    }
    case 'type':
      return step.value ? 'auto' : 'user';
    case 'select':
      return step.option ? 'auto' : 'user';
    case 'key': {
      // Enter num campo de pesquisa/filtro é consulta; noutro campo pode submeter dados
      const f = plain(
        [t.label, t.ariaLabel, t.placeholder, t.name, t.title].filter(Boolean).join(' '),
      );
      return t.type === 'search' || t.role === 'searchbox' || SEARCH_RE.test(f)
        ? 'auto'
        : 'confirm';
    }
  }
  const label = [t.text, t.ariaLabel, t.title].filter(Boolean).join(' ');
  if (!label.trim()) return 'confirm';
  return RISKY_RE.test(plain(label)) ? 'confirm' : 'auto';
}

/** Marca efetiva na execução: a escolha do autor (ou a sugerida), sempre dentro das regras de segurança. */
export function effectiveRun(step: RunStep | undefined): RunMark {
  if (!step || step.target?.sensitive) return 'user';
  // Importado e ainda não confirmado numa página real: só a pessoa sabe se é o elemento certo
  if (step.unverified && step.kind !== 'navigate' && step.kind !== 'text') return 'user';
  if (step.kind === 'type' && !step.value) return 'user';
  if (step.kind === 'select' && !step.option) return 'user';
  if (step.kind === 'navigate' || step.kind === 'tab' || step.kind === 'app' || step.kind === 'text')
    return 'auto';
  // Um atalho não precisa do elemento (vai para a app); um menu precisa do caminho
  if (step.kind === 'shortcut' && !step.shortcut?.mods.length) return 'user';
  if (step.kind === 'menu' && !step.menu?.length) return 'user';
  // Sem o elemento gravado não há onde clicar ou escrever
  if (!step.target && step.kind !== 'shortcut' && step.kind !== 'menu') return 'user';
  return step.run && RUN_MARKS.includes(step.run) ? step.run : suggestRun(step);
}

/** O guia pode ser executado? (pelo menos uma ação na página que a extensão faz) */
export function canRun(guide: Pick<Guide, 'steps'>): boolean {
  return guide.steps.some(
    (s) =>
      s.kind !== 'navigate' &&
      s.kind !== 'tab' &&
      s.kind !== 'app' &&
      s.kind !== 'text' &&
      effectiveRun(s) !== 'user',
  );
}

const pad2 = (n: number) => (n < 10 ? '0' : '') + n;

/** Data (meia-noite local) de um preset relativo a «now». */
export function presetDate(preset: DatePreset, now: Date): Date | null {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dow = (d.getDay() + 6) % 7; // segunda = 0
  switch (preset) {
    case 'today':
      return d;
    case 'yesterday':
      d.setDate(d.getDate() - 1);
      return d;
    case 'days_ago_7':
      d.setDate(d.getDate() - 7);
      return d;
    case 'days_ago_30':
      d.setDate(d.getDate() - 30);
      return d;
    case 'week_start':
      d.setDate(d.getDate() - dow);
      return d;
    case 'prev_week_start':
      d.setDate(d.getDate() - dow - 7);
      return d;
    case 'prev_week_end':
      d.setDate(d.getDate() - dow - 1);
      return d;
    case 'month_start':
      return new Date(d.getFullYear(), d.getMonth(), 1);
    case 'prev_month_start':
      return new Date(d.getFullYear(), d.getMonth() - 1, 1);
    case 'prev_month_end':
      return new Date(d.getFullYear(), d.getMonth(), 0);
    case 'year_start':
      return new Date(d.getFullYear(), 0, 1);
  }
  return null;
}

export function formatDate(d: Date, fmt: DateFormat): string {
  const dd = pad2(d.getDate());
  const mm = pad2(d.getMonth() + 1);
  const y = String(d.getFullYear());
  switch (fmt) {
    case 'aaaa-mm-dd':
      return `${y}-${mm}-${dd}`;
    case 'mm/dd/aaaa':
      return `${mm}/${dd}/${y}`;
    case 'dd-mm-aaaa':
      return `${dd}-${mm}-${y}`;
    case 'dd.mm.aaaa':
      return `${dd}.${mm}.${y}`;
  }
  return `${dd}/${mm}/${y}`;
}

/** Texto num formato → «aaaa-mm-dd» (para <input type=date>) ou '' se não for uma data válida. */
export function isoDate(text: string, fmt?: DateFormat): string {
  const m = String(text || '')
    .trim()
    .match(/^(\d{1,4})[/.-](\d{1,2})[/.-](\d{1,4})$/);
  if (!m) return '';
  const a = +m[1]!;
  const b = +m[2]!;
  const c = +m[3]!;
  let y: number, mo: number, d: number;
  if (m[1]!.length === 4) [y, mo, d] = [a, b, c];
  else if (fmt === 'mm/dd/aaaa') [mo, d, y] = [a, b, c];
  else [d, mo, y] = [a, b, c];
  const dt = new Date(y, mo - 1, d);
  if (y < 1000 || dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d)
    return '';
  return `${y}-${pad2(mo)}-${pad2(d)}`;
}

/** Valor por omissão de um passo na preparação da execução ('' = a pessoa escreve). */
export function defaultValue(value: RunValue | undefined, now = new Date()): string {
  if (!value) return '';
  if (value.kind === 'text') return value.text;
  if (value.kind === 'date') {
    const d = presetDate(value.preset, now);
    return d ? formatDate(d, value.format) : '';
  }
  return '';
}

export interface RunInput {
  /** Posição do passo no guia. */
  index: number;
  label: string;
  /** Já calculado para hoje. */
  value: string;
  kind: RunValue['kind'];
  hint: string;
}

/** Nome do campo de um passo: o título do autor, ou o rótulo do campo gravado. */
function fieldName(s: Step): string {
  const t = s.title.trim();
  if (t) return t;
  const g = s.target;
  return g?.label || g?.ariaLabel || g?.placeholder || g?.name || '';
}

/** Campos a confirmar antes de executar: os passos de escrita que a extensão preenche. */
export function runInputs(
  guide: Pick<Guide, 'steps'>,
  locale: Locale,
  now = new Date(),
): RunInput[] {
  const out: RunInput[] = [];
  guide.steps.forEach((s, i) => {
    if (s.kind !== 'type' || !s.value || effectiveRun(s) === 'user') return;
    const v = s.value;
    out.push({
      index: i,
      label: (v.kind === 'ask' && v.label) || fieldName(s) || stepLabel(i + 1, locale),
      value: defaultValue(v, now),
      kind: v.kind,
      hint:
        v.kind === 'date'
          ? datePresetLabel(v.preset, locale) + ' · ' + dateFormatLabel(v.format, locale)
          : '',
    });
  });
  return out;
}

/** Como cada passo foi feito numa execução (para o histórico). */
export type RunHow = 'auto' | 'confirm' | 'user' | 'skip' | 'back';

export interface RunLogEntry {
  guideId: string;
  title: string;
  total: number;
  startedAt: number;
  endedAt?: number;
  result?: 'done' | 'stopped' | 'replaced' | 'blocked';
  /** Só títulos e como cada passo foi feito, nunca os valores escritos. */
  steps: { n: number; title: string; how: RunHow; at: number }[];
}
